import React, { useState, useEffect } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { toast } from 'sonner';
import { Sparkles, Trash2, Edit, Eye, Plus, Loader } from 'lucide-react';
import { useBodyLock } from '../hooks/useBodyLock';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const ScenarioGenerator = () => {
  const [modules, setModules] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedModule, setSelectedModule] = useState('');

  // 🔧 FIX MOBILE: Body lock pour les modals
  useBodyLock(showGenerateModal || showViewModal);
  
  const [formData, setFormData] = useState({
    module_id: '',
    module_title: '',
    module_description: '',
    difficulty: 'Intermédiaire',
    focus_area: '',
    custom_instructions: ''
  });

  useEffect(() => {
    fetchModules();
    fetchScenarios();
  }, []);

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetchJSON(`${BACKEND_URL}/api/modules-v2`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setModules(data.modules || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchScenarios = async (moduleId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = moduleId 
        ? `${BACKEND_URL}/api/admin/scenarios/list?module_id=${moduleId}`
        : `${BACKEND_URL}/api/admin/scenarios/list`;
      
      const data = await safeFetchJSON(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setScenarios(data.scenarios || []);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      toast.error('Erreur de chargement des scénarios');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleSelect = (e) => {
    const moduleId = e.target.value;
    setFormData({ ...formData, module_id: moduleId });
    
    const module = modules.find(m => m.id === moduleId);
    if (module) {
      setFormData({
        ...formData,
        module_id: moduleId,
        module_title: module.title,
        module_description: module.description || ''
      });
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!formData.module_id || !formData.module_title) {
      toast.error('Veuillez sélectionner un module');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const data = await safeFetchJSON(`${BACKEND_URL}/api/admin/scenarios/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      toast.success('Scénario généré avec succès ! ✨');
      setShowGenerateModal(false);
      fetchScenarios(selectedModule);
      
      // Reset form
      setFormData({
        module_id: '',
        module_title: '',
        module_description: '',
        difficulty: 'Intermédiaire',
        focus_area: '',
        custom_instructions: ''
      });
    } catch (error) {
      toast.error(error.message || 'Erreur de génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (scenarioId) => {
    if (!window.confirm('Supprimer ce scénario ?')) return;

    try {
      const token = localStorage.getItem('token');
      await safeFetchJSON(`${BACKEND_URL}/api/admin/scenarios/${scenarioId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Scénario supprimé');
      fetchScenarios(selectedModule);
    } catch (error) {
      toast.error(error.message || 'Erreur de suppression');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-600" />
            Générateur de Scénarios IA
          </h2>
          <p className="text-gray-600 mt-1">
            Créez des scénarios de coaching vocal personnalisés avec l'IA
          </p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Générer un Scénario
        </button>
      </div>

      {/* Filter by Module */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-sm font-medium mb-2">Filtrer par module</label>
        <select
          value={selectedModule}
          onChange={(e) => {
            setSelectedModule(e.target.value);
            fetchScenarios(e.target.value || null);
          }}
          className="w-full px-3 py-2 border rounded-lg"
        >
          <option value="">Tous les modules</option>
          {modules.map((module) => (
            <option key={module.id} value={module.id}>
              {module.title}
            </option>
          ))}
        </select>
      </div>

      {/* Scenarios List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto text-purple-600" />
            <p className="text-gray-500 mt-2">Chargement...</p>
          </div>
        ) : scenarios.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Aucun scénario généré pour le moment
          </div>
        ) : (
          <div className="divide-y">
            {scenarios.map((scenario) => (
              <div key={scenario.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{scenario.scenario_title}</h3>
                      {scenario.generated_by_ai && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Généré par IA
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        scenario.difficulty === 'Débutant' ? 'bg-green-100 text-green-800' :
                        scenario.difficulty === 'Intermédiaire' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {scenario.difficulty}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Objectif :</strong> {scenario.goal}
                    </p>
                    
                    <p className="text-xs text-gray-500">
                      Module ID: {scenario.module_id} • 
                      Créé le {new Date(scenario.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedScenario(scenario);
                        setShowViewModal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Voir"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(scenario.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Supprimer"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-600" />
                Générer un Nouveau Scénario
              </h3>
              
              <form onSubmit={handleGenerate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Module *</label>
                  <select
                    required
                    value={formData.module_id}
                    onChange={handleModuleSelect}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner un module</option>
                    {modules.map((module) => (
                      <option key={module.id} value={module.id}>
                        {module.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Niveau de difficulté *</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Débutant">Débutant</option>
                    <option value="Intermédiaire">Intermédiaire</option>
                    <option value="Avancé">Avancé</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Zone de focus (optionnel)</label>
                  <input
                    type="text"
                    value={formData.focus_area}
                    onChange={(e) => setFormData({ ...formData, focus_area: e.target.value })}
                    placeholder="Ex: gestion de conflit, feedback constructif..."
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Instructions personnalisées (optionnel)</label>
                  <textarea
                    value={formData.custom_instructions}
                    onChange={(e) => setFormData({ ...formData, custom_instructions: e.target.value })}
                    placeholder="Instructions spécifiques pour la génération du scénario..."
                    rows={4}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowGenerateModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                    disabled={generating}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={generating}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {generating ? (
                      <>
                        <Loader className="w-5 h-5 animate-spin" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Générer avec l'IA
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedScenario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold">{selectedScenario.scenario_title}</h3>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedScenario.difficulty === 'Débutant' ? 'bg-green-100 text-green-800' :
                      selectedScenario.difficulty === 'Intermédiaire' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedScenario.difficulty}
                    </span>
                    {selectedScenario.generated_by_ai && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Généré par IA
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">🎯 Objectif</h4>
                  <p className="text-gray-800">{selectedScenario.goal}</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">📋 Prompt Coach IA</h4>
                  <p className="text-gray-800 whitespace-pre-line">{selectedScenario.prompt}</p>
                </div>

                <div className="text-xs text-gray-500 pt-4 border-t">
                  <p>Module ID: {selectedScenario.module_id}</p>
                  <p>Créé le {new Date(selectedScenario.created_at).toLocaleDateString('fr-FR')} à {new Date(selectedScenario.created_at).toLocaleTimeString('fr-FR')}</p>
                  {selectedScenario.created_by && <p>Par: {selectedScenario.created_by}</p>}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioGenerator;
